import { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  PersonAdd as PersonAddIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

// Placeholder types - these should match the actual types from src/types/index.ts
interface Athlete {
  id: string;
  name: string;
  email: string;
  consentStatus: 'approved' | 'pending' | 'revoked';
  lastActivityDate: string | null;
  ctl: number | null;
  tsb: number | null;
}

const AthletesListPage = () => {
  const [consentFilter, setConsentFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedAthlete, setSelectedAthlete] = useState<Athlete | null>(null);
  const [addAthleteDialogOpen, setAddAthleteDialogOpen] = useState<boolean>(false);
  const [newAthleteEmail, setNewAthleteEmail] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOffline, setIsOffline] = useState<boolean>(!navigator.onLine);

  // Mock data - replace with actual RTK Query hook
  const mockAthletes: Athlete[] = [
    {
      id: '1',
      name: 'John Doe',
      email: 'john.doe@example.com',
      consentStatus: 'approved',
      lastActivityDate: '2025-12-18T10:30:00Z',
      ctl: 85,
      tsb: 12,
    },
    {
      id: '2',
      name: 'Jane Smith',
      email: 'jane.smith@example.com',
      consentStatus: 'pending',
      lastActivityDate: '2025-12-15T14:20:00Z',
      ctl: 72,
      tsb: -5,
    },
    {
      id: '3',
      name: 'Mike Johnson',
      email: 'mike.johnson@example.com',
      consentStatus: 'approved',
      lastActivityDate: '2025-12-17T08:45:00Z',
      ctl: 95,
      tsb: 8,
    },
  ];

  const athletes = mockAthletes;
  const totalPages = 1;

  const handleConsentFilterChange = (_event: React.SyntheticEvent, newValue: string) => {
    setConsentFilter(newValue);
    setPage(1);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(1);
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, athlete: Athlete) => {
    setAnchorEl(event.currentTarget);
    setSelectedAthlete(athlete);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedAthlete(null);
  };

  const handleViewProfile = () => {
    if (selectedAthlete) {
      // Navigate to athlete profile page
      console.log('Navigate to athlete profile:', selectedAthlete.id);
    }
    handleMenuClose();
  };

  const handleRemoveAthlete = () => {
    if (selectedAthlete) {
      // Implement remove athlete logic
      console.log('Remove athlete:', selectedAthlete.id);
    }
    handleMenuClose();
  };

  const handleAddAthleteDialogOpen = () => {
    setAddAthleteDialogOpen(true);
  };

  const handleAddAthleteDialogClose = () => {
    setAddAthleteDialogOpen(false);
    setNewAthleteEmail('');
  };

  const handleAddAthlete = async () => {
    setIsLoading(true);
    try {
      // Implement add athlete API call
      console.log('Add athlete with email:', newAthleteEmail);
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      handleAddAthleteDialogClose();
    } catch (error) {
      console.error('Failed to add athlete:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getConsentStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckCircleIcon color="success" />;
      case 'pending':
        return <PendingIcon color="warning" />;
      case 'revoked':
        return <CancelIcon color="error" />;
      default:
        return null;
    }
  };

  const getConsentStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'revoked':
        return 'error';
      default:
        return 'default';
    }
  };

  const filteredAthletes = athletes.filter(athlete => {
    const matchesConsent = consentFilter === 'all' || athlete.consentStatus === consentFilter;
    const matchesSearch = athlete.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         athlete.email.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesConsent && matchesSearch;
  });

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {isOffline && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          You are currently offline. Some features may be limited. Changes will be synced when you're back online.
        </Alert>
      )}

      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          My Athletes
        </Typography>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={handleAddAthleteDialogOpen}
          disabled={isOffline}
        >
          Add Athlete
        </Button>
      </Box>

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search athletes by name or email..."
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        <Tabs value={consentFilter} onChange={handleConsentFilterChange}>
          <Tab label="All Athletes" value="all" />
          <Tab label="Approved" value="approved" />
          <Tab label="Pending" value="pending" />
          <Tab label="Revoked" value="revoked" />
        </Tabs>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : filteredAthletes.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No athletes found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {searchQuery ? 'Try adjusting your search criteria' : 'Add your first athlete to get started'}
          </Typography>
        </Box>
      ) : (
        <>
          <Grid container spacing={3}>
            {filteredAthletes.map((athlete) => (
              <Grid item xs={12} sm={6} md={4} key={athlete.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" component="div">
                          {athlete.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {athlete.email}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, athlete)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Chip
                        icon={getConsentStatusIcon(athlete.consentStatus)}
                        label={athlete.consentStatus.charAt(0).toUpperCase() + athlete.consentStatus.slice(1)}
                        color={getConsentStatusColor(athlete.consentStatus)}
                        size="small"
                      />
                    </Box>

                    {athlete.lastActivityDate && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Last activity: {format(new Date(athlete.lastActivityDate), 'MMM d, yyyy')}
                      </Typography>
                    )}

                    {athlete.consentStatus === 'approved' && (
                      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            CTL
                          </Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {athlete.ctl ?? 'N/A'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            TSB
                          </Typography>
                          <Typography
                            variant="body1"
                            fontWeight="bold"
                            color={athlete.tsb && athlete.tsb < 0 ? 'error.main' : 'success.main'}
                          >
                            {athlete.tsb ?? 'N/A'}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                  <CardActions>
                    <Button size="small" onClick={() => console.log('View profile:', athlete.id)}>
                      View Profile
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewProfile}>View Profile</MenuItem>
        <MenuItem onClick={handleRemoveAthlete}>Remove Athlete</MenuItem>
      </Menu>

      <Dialog open={addAthleteDialogOpen} onClose={handleAddAthleteDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Athlete</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Athlete Email"
            type="email"
            fullWidth
            variant="outlined"
            value={newAthleteEmail}
            onChange={(e) => setNewAthleteEmail(e.target.value)}
            placeholder="athlete@example.com"
            sx={{ mt: 2 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            The athlete will receive an invitation to connect with you as their trainer.
            They must approve the connection before you can access their data.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddAthleteDialogClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleAddAthlete}
            variant="contained"
            disabled={!newAthleteEmail || isLoading}
          >
            {isLoading ? <CircularProgress size={24} /> : 'Send Invitation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AthletesListPage;